import React, { useState, useEffect } from 'react';
import { 
  Mail, Plus, Edit, Trash2, Eye, Check, X, Loader2, FileText, 
  Video, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { emailTemplatesApi } from '../../lib/api';
import { toast } from 'sonner';

const EmailTemplatesManager = () => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [form, setForm] = useState({
    name: '', slug: '', type: 'custom', subject: '', subject_es: '',
    body_html: '', body_html_es: '', is_active: true
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await emailTemplatesApi.getTemplates();
      setTemplates(res.data);
    } catch (err) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      const res = await emailTemplatesApi.seedTemplates();
      toast.success(res.data.message);
      loadTemplates();
    } catch (err) {
      toast.error('Failed to seed templates');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name || '',
      slug: template.slug || '',
      type: template.type || 'custom',
      subject: template.subject || '',
      subject_es: template.subject_es || '',
      body_html: template.body_html || '',
      body_html_es: template.body_html_es || '',
      is_active: template.is_active !== false
    });
    setShowEditor(true);
  };

  const handleNew = () => {
    setEditingTemplate(null);
    setForm({
      name: '', slug: '', type: 'custom', subject: '', subject_es: '',
      body_html: getDefaultTemplate(), body_html_es: '', is_active: true
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.slug || !form.subject || !form.body_html) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingTemplate) {
        await emailTemplatesApi.updateTemplate(editingTemplate.id, form);
        toast.success('Template updated');
      } else {
        await emailTemplatesApi.createTemplate(form);
        toast.success('Template created');
      }
      setShowEditor(false);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete "${template.name}"?`)) return;
    try {
      await emailTemplatesApi.deleteTemplate(template.id);
      toast.success('Template deleted');
      loadTemplates();
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const getDefaultTemplate = () => {
    return `<div style="font-family: Georgia, serif; max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: #1F2A44; padding: 20px; text-align: center;">
        <h1 style="color: #C2A46D; margin: 0;">J. Brantley Law, PLLC</h1>
    </div>
    <div style="padding: 30px; background: #FDFCFA;">
        <h2 style="color: #1F2A44;">Your Subject Here</h2>
        <p>Dear {{name}},</p>
        <p>Your message content goes here.</p>
        
        <div style="background: #F5F3EF; padding: 20px; margin: 20px 0; border-left: 4px solid #C2A46D;">
            <p style="margin: 5px 0;"><strong>Detail:</strong> {{variable}}</p>
        </div>
        
        <p style="margin-top: 30px;">Best regards,<br><strong>Jennifer N. Brantley</strong><br>J. Brantley Law, PLLC</p>
    </div>
    <div style="background: #1F2A44; padding: 15px; text-align: center;">
        <p style="color: #888; font-size: 12px; margin: 0;">© J. Brantley Law, PLLC | The Fine Print Lawyer</p>
    </div>
</div>`;
  };

  const getTypeLabel = (type) => {
    const labels = {
      booking_confirmation: 'Booking Confirmation',
      admin_notification: 'Admin Notification',
      form_submission: 'Form Submission',
      custom: 'Custom'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      booking_confirmation: 'bg-green-100 text-green-800',
      admin_notification: 'bg-blue-100 text-blue-800',
      form_submission: 'bg-purple-100 text-purple-800',
      custom: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors.custom;
  };

  // Preview with sample data
  const getPreviewHtml = () => {
    let html = form.body_html;
    const sampleData = {
      name: 'John Smith',
      email: 'john@example.com',
      phone: '(555) 123-4567',
      date: 'March 15, 2026',
      time: '10:00 AM',
      service: 'Trademark Filing',
      zoom_link: 'https://zoom.us/j/123456789',
      state: 'Texas',
      business_name: 'Smith Enterprises LLC',
      urgency: 'Normal',
      description: 'I need help with trademark registration for my new business.',
      form_name: 'Contact Form',
      submitted_at: 'March 9, 2026 at 3:45 PM',
      responses: '<p><strong>Name:</strong> John Smith</p><p><strong>Email:</strong> john@example.com</p>',
      admin_url: 'https://jenniferbrantleylaw.com/admin'
    };
    
    Object.entries(sampleData).forEach(([key, value]) => {
      html = html.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    
    // Handle conditional zoom link
    html = html.replace(/{{#if zoom_link}}([\s\S]*?){{\/if}}/g, '$1');
    
    return html;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F2A44]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-templates-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-[#222222]">Email Templates</h2>
          <p className="text-sm text-[#555555]">Customize automated email messages</p>
        </div>
        <div className="flex gap-2">
          {templates.length === 0 && (
            <Button variant="outline" onClick={handleSeed}>
              <FileText className="w-4 h-4 mr-2" />
              Seed Default Templates
            </Button>
          )}
          <Button onClick={handleNew} className="bg-[#1F2A44]">
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded flex gap-3">
        <Video className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="font-medium text-blue-900">Add Zoom Links to Confirmations</p>
          <p className="text-sm text-blue-700">
            Use <code className="bg-blue-100 px-1 rounded">{'{{zoom_link}}'}</code> in your booking confirmation template. 
            Set your default Zoom meeting link in Email Settings.
          </p>
        </div>
      </div>

      {/* Template List */}
      {!showEditor && (
        <div className="space-y-3">
          {templates.length === 0 ? (
            <div className="bg-white border border-[#E8E4DC] rounded p-12 text-center">
              <Mail className="w-12 h-12 text-[#C2A46D] mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Email Templates</h3>
              <p className="text-[#555555] mb-4">Create templates for automated emails.</p>
            </div>
          ) : (
            templates.map((template) => (
              <div key={template.id} className="bg-white border border-[#E8E4DC] rounded p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#F5F3EF] rounded flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#1F2A44]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-[#222222]">{template.name}</h3>
                      <Badge className={getTypeColor(template.type)}>{getTypeLabel(template.type)}</Badge>
                      {!template.is_active && <Badge variant="outline" className="text-red-600">Inactive</Badge>}
                    </div>
                    <p className="text-sm text-[#555555]">Subject: {template.subject}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setEditingTemplate(template); setForm({ ...template }); setShowPreview(true); }}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(template)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(template)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Editor */}
      {showEditor && (
        <div className="bg-white border border-[#E8E4DC] rounded p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{editingTemplate ? 'Edit Template' : 'New Template'}</h3>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Template Name *</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Slug *</Label>
              <Input value={form.slug} onChange={(e) => setForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} className="mt-1" disabled={!!editingTemplate} />
            </div>
            <div>
              <Label>Subject (English) *</Label>
              <Input value={form.subject} onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))} className="mt-1" placeholder="e.g., Consultation Confirmation - J. Brantley Law" />
            </div>
            <div>
              <Label>Subject (Spanish)</Label>
              <Input value={form.subject_es} onChange={(e) => setForm(p => ({ ...p, subject_es: e.target.value }))} className="mt-1" />
            </div>
          </div>

          {/* Available Variables */}
          <div className="bg-[#F5F3EF] p-4 rounded">
            <p className="font-medium text-sm mb-2">Available Variables:</p>
            <div className="flex flex-wrap gap-2">
              {['{{name}}', '{{email}}', '{{phone}}', '{{date}}', '{{time}}', '{{service}}', '{{zoom_link}}', '{{state}}', '{{business_name}}', '{{description}}'].map(v => (
                <code key={v} className="bg-white px-2 py-1 rounded text-xs border cursor-pointer hover:bg-[#1F2A44] hover:text-white" onClick={() => setForm(p => ({ ...p, body_html: p.body_html + v }))}>{v}</code>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Email Body (HTML) *</Label>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(!showPreview)}>
                <Eye className="w-4 h-4 mr-1" /> {showPreview ? 'Hide' : 'Show'} Preview
              </Button>
            </div>
            <div className={showPreview ? 'grid grid-cols-2 gap-4' : ''}>
              <Textarea value={form.body_html} onChange={(e) => setForm(p => ({ ...p, body_html: e.target.value }))} className="font-mono text-sm" rows={15} />
              {showPreview && (
                <div className="border rounded p-4 overflow-auto bg-white" style={{ maxHeight: '400px' }}>
                  <div dangerouslySetInnerHTML={{ __html: getPreviewHtml() }} />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Switch checked={form.is_active} onCheckedChange={(checked) => setForm(p => ({ ...p, is_active: checked }))} />
            <Label>Template is Active</Label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#1F2A44]">
              <Check className="w-4 h-4 mr-2" />
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && !showEditor && editingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Preview: {editingTemplate.name}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-4">
              <div dangerouslySetInnerHTML={{ __html: getPreviewHtml() }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesManager;
